import { sessionExpired } from '../stores/stores';

const baseUrl = 'https://backend.lekplatskartan.se/api/v1'
//const baseUrl = 'http://localhost:8080'
const apiKey = 'f5080e438388a006867a06b4e4fc483d'
const apiSecret = '56e4a8a410456a2553a33da12edbb569'

// The app description to send to the server to indicate our "API level"
const appString = 'Playway/1.2.4';

/**
 * The error a request fails with when the session has expired. Callers can recognize it to
 * leave the reporting to the app, which takes the user back to the login form.
 */
export class SessionExpiredError extends Error {
    constructor() {
        super('The session has expired');
        this.name = 'SessionExpiredError';
    }
}

const ApiClient = {

    /**
     * Checks if we have an ongoing session. If there is a session, the user
     * object for the session is returned, otherwise null is returned.
     */
    getSessionUser: async function () {
        return fetch(baseUrl + '/session', {
            credentials: 'include',
            cache: 'no-store',
            headers: getHeaders()
        }).then(response => {
            return response.ok ? response.json() : null;
        })
    },


    updateUser: async function (user, data) {
        return putJson(`/users/${user._id}/`, data);
    },


    /**
     * Attempts to log in using the specified credentials.
     * 
     * @param {String} username 
     * @param {String} password 
     */
    login: async function (username, password) {
        let data = {
            username: username,
            password: password
        }

        return fetch(baseUrl + '/session', {
            method: 'POST',
            credentials: 'include',
            headers: getJsonHeaders(),
            body: JSON.stringify(data)
        })
    },


    /**
     * Logs out of the current session.
     */
    logout: async function () {
        return fetch(baseUrl + '/session', {
            method: 'DELETE',
            credentials: 'include',
            headers: getHeaders()
        }).then(response => {
            return response.ok ? true : false;
        })
    },


    /**
     * Loads an array of draft playgrounds.
     * 
     * @returns {Array} A potentially empty array of playground objects.
     */
    loadDraftPlaygrounds: async function () {
        return getJson('/playgrounds?query=drafts');
    },


    /**
     * Loads a single playground.
     * 
     * @param {String} id 
     * @returns {Object} The playground, if found
     */
    loadPlayground: async function (id) {
        return getJson(`/playgrounds/${id}`);
    },


    /**
     * Loads any playgrounds within the specified distance from the specified location.
     * 
     * @param {Number} lat The latitude of the location
     * @param {Number} lon The longitude of the location
     * @param {Number} distance The maximum distance from the location to look for playgrounds, in meters, as an integer.
     * @returns {Array} A potentially empty array of playground objects.
     */
    loadPlaygroundsNear: async function (lat, lon, distance) {
        distance = parseInt(distance) || 5000;
        return getJson(`/playgrounds/?query=near&location=${lat},${lon}&maxdistance=${distance}`);
    },


    createPlayground: async function (data) {
        return postJson('/playgrounds/', data);
    },


    updatePlayground: async function (playground, data) {
        if (!data) {
            data = playground;
        }

        return putJson(`/playgrounds/${playground._id}/`, data);
    },


    approvePlayground: async function (playground) {
        return this.setPlaygroundState(playground, 'published');
    },


    rejectPlayground: async function (playground) {
        return this.setPlaygroundState(playground, 'rejected');
    },


    /**
     * Sets the state of the specified playground. The local playground object is only
     * updated once the server has accepted the change, so that a failed request leaves it
     * as it was and the change can be retried.
     *
     * @param {Object} playground
     * @param {String} state
     */
    setPlaygroundState: async function (playground, state) {
        // This can happen if we're importing draft playgrounds
        if (!playground._id) {
            console.log(`Attempting to set state ${state} of playground without id`);
            return Promise.reject(new Error('Playground has no id'));
        }

        return putJson(`/playgrounds/${playground._id}/`, { ...playground, state: state })
            .then(updated => {
                playground.state = state;
                return updated;
            });
    },


    deletePlayground: async function (playground) {
        return fetch(baseUrl + `/playgrounds/${playground._id}/`, {
            method: 'DELETE',
            headers: getJsonHeaders(),
            credentials: 'include'
        }).then(checkSession);
    },


    mergePlaygrounds: async function (playgrounds) {
        let idString = playgrounds.map(p => p._id).join(',')

        return fetch(baseUrl + `/playgrounds/merge?playgrounds=${idString}`, {
            method: 'POST',
            headers: getJsonHeaders(),
            credentials: 'include'
        }).then(checkSession);
    },


    loadEdit: async function (id) {
        if (!id) {
            return Promise.reject();
        }

        return getJson(`/edits/${id}`);
    },

    loadEdits: async function () {
        return getJson('/edits?query=open')
    },

    approveEdit: async function (edit) {
        return this.setEditState(edit, 'approved');
    },

    rejectEdit: async function (edit) {
        return this.setEditState(edit, 'rejected');
    },

    /**
     * Sets the state of the specified edit. The local edit object is only updated once the
     * server has accepted the change, so that a failed request leaves it as it was and the
     * sidebar keeps the item.
     *
     * @param {Object} edit
     * @param {String} state
     */
    setEditState: async function (edit, state) {
        let data = { ...edit, metadata: { ...edit.metadata, state: state } };

        return putJson(`/edits/${edit._id}/`, data).then(updated => {
            edit.metadata.state = state;
            return updated;
        });
    },

    createEdit: async function (playground, data) {
        return postJson(`/playgrounds/${playground._id}/edits`, data);
    },

    loadDraftPhotos: async function () {
        return getJson('/photos?query=draft');
    },

    loadPhotos: async function (playgroundId) {
        return getJson(`/playgrounds/${playgroundId}/photos?state=draft,published`)
    },

    getThumbnailUrl: function (photoId, size) {
        return baseUrl + `/photos/${photoId}/download?width=${size}&height=${size}`;
    },

    getPhotoUrl: function (photoId, size) {
        return baseUrl + `/photos/${photoId}/download?width=${size}&height=${size}&fit=inside`;
    },

    loadOperators: async function () {
        return getJson('/operators');
    },

    /**
     * Searches for places and playgrounds by name, the same search the apps and the web app
     * use. The location is what the results are sorted around, nearest first, so the map's
     * own centre is what to pass: searching for a name that exists in several places then
     * finds the one being looked at rather than the one with the largest population.
     *
     * Only published playgrounds are searchable, since that is what the search index on the
     * server covers. Drafts and edits are found in the inbox instead.
     *
     * @param {String} query The name to search for. The server refuses anything shorter
     *                       than three characters.
     * @param {Number} lat The latitude to sort the results around.
     * @param {Number} lon The longitude to sort the results around.
     * @returns {Object} The search, with the matches in its `results` property and the
     *                   country the location belongs to in `userCountry`.
     */
    searchPlaces: async function (query, lat, lon) {
        let params = new URLSearchParams({
            query: query,
            near: `${lat.toFixed(6)},${lon.toFixed(6)}`,
        });

        return getJson(`/places/search?${params.toString()}`);
    },

    /**
     * Uploads photos of the specified playground.
     * 
     * @param {Object} playground 
     * @param {Array} files 
     * @returns {Promise}
     */
    uploadPhotos: async function (playground, files) {
        if (!playground) {
            return Promise.reject('No playground');
        }

        var formData = new FormData();
        files.forEach(file => formData.append('files[]', file));

        return fetch(baseUrl + `/playgrounds/${playground._id}/photos`, {
            method: 'POST',
            credentials: 'include',
            headers: getHeaders(),
            body: formData
        }).then(checkResponse)
    },

    /**
     * Sets the state of the specified photo to 'published'.
     *
     * @param {Object} photo
     */
    approvePhoto: async function (photo) {
        return this.setPhotoState(photo, 'published');
    },

    /**
     * Sets the state of the specified photo to 'removed'.
     *
     * @param {Object} photo
     */
    rejectPhoto: async function (photo) {
        return this.setPhotoState(photo, 'removed');
    },

    /**
     * Sets the state of all the specified photos to 'published'.
     *
     * @param {Array} photos
     */
    approvePhotos: async function (photos) {
        return Promise.all(photos.map(photo => this.approvePhoto(photo)));
    },

    /**
     * Sets the state of all the specified photos to 'removed'.
     *
     * @param {Array} photos
     */
    rejectPhotos: async function (photos) {
        return Promise.all(photos.map(photo => this.rejectPhoto(photo)));
    },

    /**
     * Sets the state of the specified photo to 'draft'.
     *
     * @param {Object} photo
     */
    draftPhoto: async function (photo) {
        return this.setPhotoState(photo, 'draft');
    },

    /**
     * Sets the state of the specified photo. The local photo object is only updated once
     * the server has accepted the change, so that a failed request leaves it as it was and
     * the sidebar keeps the item.
     *
     * @param {Object} photo
     * @param {String} state
     */
    setPhotoState: async function (photo, state) {
        return putJson(`/photos/${photo._id}`, { ...photo, state: state })
            .then(updated => {
                photo.state = state;
                return updated;
            });
    },


    rotatePhoto: async function (photo, clockwise) {
        return fetch(baseUrl + `/photos/${photo._id}/rotate?clockwise=${clockwise ? 'true' : 'false'}`, {
            method: 'POST',
            headers: getJsonHeaders(),
            credentials: 'include'
        }).then(checkResponse).then(response => {
            return response.json()
        })
    },


    loadPhotoEdits: async function () {
        return getJson('/photo-edits?query=open');
    },

    approvePhotoEdit: async function (photoEdit) {
        return this.setPhotoEditState(photoEdit, 'approved');
    },

    rejectPhotoEdit: async function (photoEdit) {
        return this.setPhotoEditState(photoEdit, 'rejected');
    },

    setPhotoEditState: async function (photoEdit, state) {
        return putJson(`/photo-edits/${photoEdit._id}`, { metadata: { state } });
    },

    loadSidebarData: async function () {
        return new Promise((resolve, reject) => {
            let items = [];
            var callsLeft = 0;

            let callback = () => {
                callsLeft--;
                if (callsLeft == 0) {
                    resolve(items);
                }
            };

            // A part that fails is logged and left out, so that the rest of the sidebar
            // still loads instead of waiting for something that will never arrive.
            let failed = (name) => {
                return (err) => {
                    console.log(`Failed to load ${name}`, err);
                    callback();
                };
            };

            callsLeft++;
            ApiClient.loadDraftPlaygrounds().then((playgrounds) => {
                console.log('Got playgrounds');
                items.push(
                    ...playgrounds.map((p) => {
                        return {
                            ...p,
                            date: p.created,
                            isPlayground: true,
                        };
                    })
                );

                callback();
            }).catch(failed('playgrounds'));

            callsLeft++;
            ApiClient.loadEdits().then((edits) => {
                console.log('Got edits');
                items.push(
                    ...edits.map((e) => {
                        return {
                            ...e,
                            date: e.metadata.created,
                            isEdit: true,
                        };
                    })
                );

                callback();
            }).catch(failed('edits'));

            callsLeft++;
            ApiClient.loadDraftPhotos().then(photos => {
                console.log('Got photos');
                items.push(
                    ...photos.filter(p => p.playground != null && p.playground.state == 'published').map(p => {
                        return {
                            ...p,
                            date: p.created,
                            isPhoto: true
                        }
                    })
                )

                callback();
            }).catch(failed('photos'))

            callsLeft++;
            ApiClient.loadPhotoEdits().then(photoEdits => {
                console.log('Got photo edits');
                items.push(
                    ...photoEdits.map(e => {
                        return {
                            ...e,
                            date: e.metadata.created,
                            isPhotoEdit: true
                        }
                    })
                )

                callback();
            }).catch(failed('photo edits'))
        });
    }
}


async function getJson(path) {
    return fetch(baseUrl + path, {
        headers: getHeaders(),
        credentials: 'include'
    }).then(checkResponse).then(response => {
        return response.json();
    })
}


async function putJson(path, data) {
    return sendJson('PUT', path, data);
}


async function postJson(path, data) {
    return sendJson('POST', path, data);
}


async function sendJson(verb, path, data) {
    return fetch(baseUrl + path, {
        method: verb,
        headers: getJsonHeaders(),
        credentials: 'include',
        body: JSON.stringify(data)
    }).then(checkResponse).then(response => {
        return response.json()
    })
}


/**
 * Checks that a request succeeded. A response that isn't ok is turned into a rejection, so
 * that callers don't mistake an error page for the data they asked for, and an unauthorized
 * one means that the session has expired.
 *
 * @param {Response} response The response to check.
 * @returns {Promise} The response, or a rejection describing the failure.
 */
function checkResponse(response) {
    if (response.status === 401) {
        return expireSession();
    }

    if (!response.ok) {
        return Promise.reject(new Error(`Request failed: ${response.status} ${response.statusText}`));
    }

    return response;
}


/**
 * Checks whether a response means that the session has expired, for the requests that hand
 * the response itself to their caller rather than the data it holds.
 *
 * @param {Response} response The response to check.
 * @returns {Promise} The response, or a rejection if the session has expired.
 */
function checkSession(response) {
    return response.status === 401 ? expireSession() : response;
}


/**
 * Notes that the session has expired, so that the app can take the user back to the login
 * form. The server answers every request needing a session with 401 once the session is
 * gone, so a single unauthorized response is enough to know that a new login is needed.
 *
 * @returns {Promise} A rejection describing the expired session.
 */
function expireSession() {
    sessionExpired.set(true);
    return Promise.reject(new SessionExpiredError());
}


/**
 * Get common headers for all requests.
 */
function getHeaders() {
    let headers = new Headers();
    headers.set('Authorization', 'Basic ' + btoa(apiKey + ":" + apiSecret));
    headers.set('X-Client', appString);
    return headers;
}


/**
 * Get common headers for all requests sending json data.
 */
function getJsonHeaders() {
    let headers = getHeaders();
    headers.set('Content-Type', 'application/json');
    return headers;
}


export default ApiClient;